import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification } from '@testflow/shared';
import { deeplinkForNotification } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatNotificationTime, notificationLines } from '../lib/notification-lines.js';
import { notificationsApi } from '../lib/notifications-api.js';
import { scopeUserToProject } from '../lib/project-roles.js';
import { useSession } from '../lib/session.js';

// NOT-3 (in-app): Glocke in der vollen Header-Nav UND (#76) in der Tester-Minimal-Shell —
// Ungelesen-Zähler = Anzahl read_at===null (Design-Spec §2.1 TopNav-Zähler-Badge Orange).
// Dropdown listet die neuesten Einträge (Server liefert max. 50, neueste zuerst); Klick markiert
// gelesen + navigiert zum Deeplink des Kinds.
//
// #76/I1: `deeplinkForNotification` liegt in @testflow/shared, damit Client und die Mail
// (dispatch.ts) garantiert dieselbe Regel anwenden — vorher zeigte `defect_assigned` in der Mail
// immer auf `/defects/:id`, eine Route, die ein Empfaenger ohne `defect.edit` gar nicht kennt.
//
// #23: die Glocke ist global (nicht an ein aktuelles Projekt gebunden) und listet
// Benachrichtigungen ÜBER Projekte hinweg — anders als die restlichen Leser kann sie deshalb
// nicht `useScopedUser()` (scopt auf das Projekt der URL) verwenden. Jede Notification trägt ihr
// eigenes `project_id`; `deeplinkForNotification` bekommt den auf GENAU DIESES Projekt
// beschnittenen Nutzer, sonst würde `defect.edit` in Projekt A einen Deeplink für eine
// Benachrichtigung aus Projekt B freischalten, den der Server dort mit 403 abweist.
//
// #202: jeder Eintrag zweizeilig — Zeile 1 Kurzform + ID (fett bei ungelesen) mit relativer Zeit
// rechts, Zeile 2 der Titel (höchstens zwei Zeilen). Der Textbau liegt in `lib/notification-lines.ts`;
// die Zeile 1 wird bei Platzmangel gekürzt, der volle Text steht dann im `title`.
// Review-Nachschliff: die Liste scrollt ab 70vh (Kopf bleibt stehen); für Screenreader trennen
// unsichtbare Kommas Hinweis „ungelesen", Zeile 1, Zeit (`<time datetime>`) und Titel.

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const lang: 'de' | 'en' = i18n?.resolvedLanguage === 'en' ? 'en' : 'de';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationsApi.list,
    refetchInterval: 60_000,
  });
  const notifications = data ?? [];
  const unreadCount = notifications.filter((n) => n.read_at === null).length;

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleSelect = (n: Notification) => {
    markRead.mutate(n.notification_id);
    setOpen(false);
    navigate(deeplinkForNotification(n, scopeUserToProject(user, n.project_id)));
  };

  const handleMarkAllRead = () => {
    for (const n of notifications) {
      if (n.read_at === null) markRead.mutate(n.notification_id);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('notif.title')}
        className="relative rounded-chip border border-white/20 px-2 py-1 text-xs text-white/80 hover:text-white"
      >
        🔔
        {unreadCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-accent px-1 text-[10px] font-bold leading-none text-white">
            {unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-2 w-96 max-w-[calc(100vw-1rem)] rounded-card border border-[var(--border)] bg-bg-surface p-2 text-left shadow-lg">
          <div className="mb-1 flex items-center justify-between px-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              {t('notif.title')}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-[10px] text-brand-accent hover:underline"
              >
                {t('notif.markAllRead')}
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="p-2 text-xs text-text-muted">{t('notif.empty')}</div>
          ) : (
            <ul className="flex max-h-[70vh] flex-col gap-1 overflow-y-auto">
              {notifications.map((n) => {
                const { headline, detail } = notificationLines(t, i18n?.resolvedLanguage, n);
                const unread = n.read_at === null;
                const time = formatNotificationTime(new Date(n.created_at), new Date(), lang, t);
                return (
                  <li key={n.notification_id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(n)}
                      className="w-full rounded-chip px-2 py-1 text-left text-xs hover:bg-bg-subtle"
                    >
                      {unread && <span className="sr-only">{t('notif.unread')}, </span>}
                      <span className="flex items-baseline justify-between gap-2">
                        <span
                          title={headline}
                          className={`truncate ${
                            unread ? 'font-semibold text-text-primary' : 'text-text-muted'
                          }`}
                        >
                          {headline}
                        </span>
                        {time && (
                          <>
                            <span className="sr-only">, </span>
                            <time
                              dateTime={n.created_at}
                              className="shrink-0 text-[10px] text-text-muted"
                            >
                              {time}
                            </time>
                          </>
                        )}
                      </span>
                      {detail && (
                        <>
                          <span className="sr-only">, </span>
                          <span className="mt-0.5 line-clamp-2 block break-words text-text-muted">
                            {detail}
                          </span>
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
